// frontend/packages/business-console/src/pages/Quotes/useQuotesList.js
//
// Data hook for the Quotes list page. Two modes:
//
//   - Demo (`?demo=1` query string OR
//     `localStorage["pulse.demo"] === "1"`): returns the handcrafted
//     fixtures from `./demoData.js`. Used for marketing screenshots
//     without touching real tenant data. Filter + search apply on the
//     client-side fixtures so the UI behaves identically to real-data
//     mode.
//
//   - Real (default): calls `/api/v2/crm/quotes` via `listQuotes` from
//     `services/quoteService.js`. Filters / search / cursor pagination
//     pass through as query params.
//
// Shape returned: { items, hasMore, nextCursor, loading, error,
// loadMore, refresh, isDemo }.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { listQuotes } from '../../services/quoteService';
import { demoQuotesList } from './demoData';

function readDemoFlag(searchString) {
  const params = new URLSearchParams(searchString || '');
  if (params.get('demo') === '1') return true;
  try {
    return localStorage.getItem('pulse.demo') === '1';
  } catch {
    return false;
  }
}

function filterDemoItems(items, { state, q }) {
  let out = items;
  if (state && state !== 'all') {
    out = out.filter((x) => x.state === state);
  }
  if (q && q.trim()) {
    const needle = q.trim().toLowerCase();
    out = out.filter(
      (x) =>
        x.number.toLowerCase().includes(needle) ||
        (x.customerName || '').toLowerCase().includes(needle)
    );
  }
  return out;
}

export function useQuotesList({ state, q } = {}) {
  const location = useLocation();
  const isDemo = useMemo(
    () => readDemoFlag(location.search),
    [location.search]
  );

  const [items, setItems] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const loadFirstPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (isDemo) {
        const filtered = filterDemoItems(demoQuotesList.items, { state, q });
        setItems(filtered);
        setHasMore(false);
        setNextCursor(null);
      } else {
        const params = {};
        if (state && state !== 'all') params.state = state;
        if (q && q.trim()) params.q = q.trim();
        const res = await listQuotes(params);
        setItems(res.items || []);
        setHasMore(Boolean(res.hasMore));
        setNextCursor(res.nextCursor || null);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isDemo, state, q]);

  const loadMore = useCallback(async () => {
    if (!hasMore || !nextCursor || isDemo) return;
    setLoadingMore(true);
    try {
      const params = { before: nextCursor };
      if (state && state !== 'all') params.state = state;
      if (q && q.trim()) params.q = q.trim();
      const res = await listQuotes(params);
      setItems((prev) => [...prev, ...(res.items || [])]);
      setHasMore(Boolean(res.hasMore));
      setNextCursor(res.nextCursor || null);
    } catch (err) {
      setError(err);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, nextCursor, isDemo, state, q]);

  useEffect(() => {
    loadFirstPage();
  }, [loadFirstPage]);

  return {
    items,
    hasMore,
    nextCursor,
    loading,
    loadingMore,
    error,
    loadMore,
    refresh: loadFirstPage,
    isDemo,
  };
}
